/**
 * @see https://stackoverflow.com/a/30527650/5219328
 */

const getScrollStep = (
  targetPosition: number,
  currentPosition: number,
  scrollRate: number,
) => Math.ceil((targetPosition - currentPosition) / scrollRate);

const isTargetPositionReached = (
  targetPosition: number,
  currentPosition: number,
) =>
  currentPosition === targetPosition ||
  getPositionsDiff(targetPosition, currentPosition) < 1;

const getPositionsDiff = (targetPosition: number, currentPosition: number) =>
  Math.abs(currentPosition - targetPosition);

export enum AXIS {
  v = "v",
  h = "h",
}

type SmoothScrollOptions = {
  container: Element;
  targetScrollPosition: number;
  axis: AXIS;
  scrollRate: number;
  onAnimationEnd?: () => void;
};

export const makeScroller = () => {
  let isScrolling = false;

  return ({ onAnimationEnd, ...restOptions }: SmoothScrollOptions) => {
    if (isScrolling) {
      return;
    }

    isScrolling = true;
    const handleAnimationEnd = () => {
      isScrolling = false;

      if (typeof onAnimationEnd === "function") {
        onAnimationEnd();
      }
    };

    smoothScroll({ onAnimationEnd: handleAnimationEnd, ...restOptions });
  };
};

const smoothScroll = ({
  container,
  targetScrollPosition,
  axis = AXIS.v,
  scrollRate = 5,
  onAnimationEnd,
}: SmoothScrollOptions & { onAnimationEnd: () => void }) => {
  if (!container) {
    return;
  }

  const scrollProperty = axis === "v" ? "scrollTop" : "scrollLeft";
  const currentScrollPosition = container[scrollProperty] || 0;

  if (isTargetPositionReached(targetScrollPosition, currentScrollPosition)) {
    onAnimationEnd();
    return;
  }

  const diff = getPositionsDiff(targetScrollPosition, currentScrollPosition);
  if (diff < scrollRate) {
    container[scrollProperty] = targetScrollPosition;
  } else {
    const step = getScrollStep(
      targetScrollPosition,
      currentScrollPosition,
      scrollRate,
    );
    container[scrollProperty] = currentScrollPosition + step;
  }

  window.requestAnimationFrame(() => {
    smoothScroll({
      container,
      targetScrollPosition,
      axis,
      scrollRate,
      onAnimationEnd,
    });
  });
};
